import React, { Fragment, useState, useRef, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import AsyncSelect from 'react-select/async'
import School from '../../../../models/School'
import { schoolLabels } from '../../../../utils/schoolLabel'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'
import Modal from '../../../common/Modal';
import Button from "../../../Button";
import makeAnimated from 'react-select/animated';
import styles from './StudentImport.module.sass'
import HeaderMatcher from './HeaderMatcher'
import CreatableSelect from 'react-select/creatable';
import { clearUnreadableStaffFile } from '../../../../utils/studentImportStaffFile';

const StudentImport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [headers, setHeaders] = useState([])
  const [staffHeaders, setStaffHeaders] = useState([])
  const [seasons, setSeasons] = useState([])
  const [school, setSchool] = useState()
  const [formData, setFormData] = useState()
  const form = useRef(null)

  useEffect(() => {
    if (school && school.value) {
      fetch(`/admin/schools/${school.value}/seasons`,
        { method: 'GET' })
        .then(res => res.json())
        .then(json => {
          const seasons = json.map((season) => ({ value: season, label: season }))
          setSeasons(seasons);
        })
    }
  }, [school])

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setHeaders([])
    setStaffHeaders([])
    setSeasons([])
    setIsOpen(false)
    setIsSubmitting(false)
    setFormData()
  };
  const { register, handleSubmit, control, setValue } = useForm();

  const onSubmit = (data) => {
    setIsSubmitting(true)
    if (!data.csv || !data.school || !data.season) {
      toast('Please select a school, season, and CSV before continuing', { type: 'error' })
      setIsSubmitting(false)
      return;
    }

    setSchool(data.school)
    const formData = new FormData(form.current)

    fetch('/admin/student_imports/get_headers',
      { method: 'POST', body: formData })
      .then(res => res.json())
      .then(json => {
        const headers = json.data;
        if (headers.length === 0) {
          toast('There were no headers found in your CSV, or it might be malformed. Please try uploading another file.',
            { type: 'error' })
          setIsSubmitting(false)
          return;
        }
        const staffFileWarning = clearUnreadableStaffFile(formData, json)
        if (staffFileWarning) toast(staffFileWarning, { type: 'warning' })
        setHeaders(headers)
        setStaffHeaders(json.staff_data || [])
        setFormData(formData)
      })
      .catch(() => {
        toast('There was an error processing the CSV file. Please try again or use a different file.',
          { type: 'error' })
        setIsSubmitting(false)
      })
  }

  const loadSchoolOptionsQuery = () => (value, callback) => {
    School
      .where({
        sales_stat: { match: 'CUST' },
        name: { match: value },
      })
      .order({ name: 'asc' })
      .all()
      .then((res) => {
        const labels = schoolLabels(res.data)
        const options = res.data.map((e, i) => ({ label: labels[i], value: e.id, data: e }))
        callback(options)
      })
  }

  const renderForm = () => {
    if (headers.length > 0) {
      return (
        <HeaderMatcher
          school={school}
          formData={formData}
          headers={headers}
          staffHeaders={staffHeaders}
          closeModal={closeModal}
        />
      )
    }

    return (
      <form ref={form} onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Controller
          control={control}
          name="school"
          render={({ field, value }) => (
            <AsyncSelect
              {...field}
              cacheOptions
              defaultOptions
              className={styles.input}
              placeholder="Select school"
              name={name}
              onChange={(e) => { setSchool(e); setValue('school', e) }}
              loadOptions={loadSchoolOptionsQuery()}
            />
          )}
        />

        <Controller
          control={control}
          name="season"
          render={({ field, value }) => (
            <CreatableSelect
              {...field}
              isClearable
              onChange={(e) => setValue('season', e)}
              options={seasons}
              placeholder="Select season (type to create a new season)"
            />
          )}
        />

        <label className={styles.label} style={{ display: 'block', margin: '10px 0' }}>
          <input
            {...register("staff_file")}
            type="checkbox" />

          <span>
            {' '}This is a staff file (every row imports as staff — no grade column needed)
          </span>
        </label>

        <label className={styles.label}>
            <span>
              CSV File
            </span>

          <input
            {...register("csv", { required: true, message: "Please select a CSV to upload" })}
            className={styles.input}
            type="file" />
        </label>

        <label className={styles.label}>
            <span>
              Companion staff/teacher file (optional) — if you have both, upload them together:
              the roster imports first, then this file imports automatically as staff, and
              we&apos;ll cross-check that every classroom teacher is grouped with their own class
            </span>

          <input
            {...register("staff_csv")}
            className={styles.input}
            type="file" />
        </label>

        <div className={styles.formActions}>
          <Button
            fullWidth
            variant='primary'
            disabled={isSubmitting}
          >
            Continue
          </Button>
        </div>
      </form>
    )
  }

  return (
    <Fragment>
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Modal
        modalIsOpen={isOpen}
        closeModal={closeModal}
        contentLabel="Import CSV"
        headerText="Import CSV"
      >
        {renderForm()}
      </Modal>

      <Button onClick={openModal}>
        Upload CSV
      </Button>
    </Fragment>
  )
}

export default StudentImport;
